/**
 * Hook for handling Trust Flow 3DS authentication
 */
import { useCallback, useRef } from 'react';
import type { Payment, PaymentOptions } from '../../../core/resources/payments';
import type { UsePaymentOptions } from '../usePaymentQuery';

interface UseTrustFlowActionParams {
    setError: (error: string | null) => void;
    setIsLoading: (loading: boolean) => void;
    hookOptionsRef: React.MutableRefObject<UsePaymentOptions | undefined>;
}

interface TrustFlowAuthData {
    appId: string;
    txnId: string;
    hash: string;
    captureUrl: string;
    returnUrl: string;
    testMode: boolean;
}

export function useTrustFlowAction({
    setError,
    setIsLoading,
    hookOptionsRef,
}: UseTrustFlowActionParams) {
    const challengeInProgressRef = useRef(false);

    const handleTrustFlowAuth = useCallback(
        async (payment: Payment, actionData: any, options: PaymentOptions = {}) => {
            if (challengeInProgressRef.current) {
                console.log('Trust Flow 3DS challenge already in progress');
                return;
            }

            const authData = actionData?.metadata?.trustflow as TrustFlowAuthData | undefined;

            if (!authData?.appId || !authData?.txnId || !authData?.hash) {
                const errorMsg = 'Missing Trust Flow 3DS authentication data';
                console.error(errorMsg);
                setError(errorMsg);
                setIsLoading(false);
                options.onFailure?.(errorMsg);
                options.onPaymentFailed?.({
                    code: 'TRUSTFLOW_MISSING_DATA',
                    message: errorMsg,
                    payment,
                });
                return;
            }

            try {
                challengeInProgressRef.current = true;
                setIsLoading(false); // Stop loading before redirect

                console.log('🔐 [Trust Flow] Starting 3DS authentication:', {
                    appId: authData.appId,
                    txnId: authData.txnId,
                    captureUrl: authData.captureUrl,
                    testMode: authData.testMode,
                });

                // Create form for Trust Flow 3DS capture request
                const form = document.createElement('form');
                form.method = 'POST';
                form.action = authData.captureUrl;
                form.style.display = 'none';

                // Add required fields for Trust Flow capture
                const appIdInput = document.createElement('input');
                appIdInput.type = 'hidden';
                appIdInput.name = 'APP_ID';
                appIdInput.value = authData.appId;
                form.appendChild(appIdInput);

                const txnIdInput = document.createElement('input');
                txnIdInput.type = 'hidden';
                txnIdInput.name = 'TXN_ID';
                txnIdInput.value = authData.txnId;
                form.appendChild(txnIdInput);

                const hashInput = document.createElement('input');
                hashInput.type = 'hidden';
                hashInput.name = 'HASH';
                hashInput.value = authData.hash;
                form.appendChild(hashInput);

                // Add form to document and submit
                document.body.appendChild(form);
                form.submit();

                console.log('🔐 [Trust Flow] Redirecting to 3DS challenge...');

                // Note: Success callback will be triggered after redirect return
                // Payment status will be updated via webhook

            } catch (error) {
                challengeInProgressRef.current = false;
                const errorMsg = error instanceof Error ? error.message : 'Failed to process Trust Flow 3DS authentication';
                console.error('🔐 [Trust Flow] 3DS authentication error:', error);
                setError(errorMsg);
                setIsLoading(false);
                options.onFailure?.(errorMsg);
                options.onPaymentFailed?.({
                    code: 'TRUSTFLOW_AUTH_FAILED',
                    message: errorMsg,
                    payment,
                });

                // Hook-level callback (universal handler)
                if (hookOptionsRef.current?.onPaymentFailed) {
                    hookOptionsRef.current.onPaymentFailed(errorMsg, {
                        isRedirectReturn: false,
                    });
                }
            }
        },
        [setError, setIsLoading, hookOptionsRef],
    );

    return { handleTrustFlowAuth };
}
